import { createApi } from "@reduxjs/toolkit/query/react";
import { useDispatch } from "react-redux";

import {
    UserExternalRewardsFilter,
    UserLendingVaultFilter,
    UserVaultExternalRewardsPnlResponse,
    UserPositionDepositSummaryResponse,
    UserPositionDepositSummaryParams,
    UpdateVaultMetadataParams
} from "../types";
import { ApiVaultDepositResponse } from "../types/api";
import { VaultDepositResponse } from "../types/domain";
import { parseVaultDepositResponseFromApi } from "../types/parsers";
import { abfSerializeQueryArgs, marketsBaseQuery } from "./util";
import {
    ROUTE_GET_LENDING_USER_VAULT_POSITIONS,
    ROUTE_GET_USER_EXTERNAL_REWARDS_PNL,
    ROUTE_GET_USER_POSITION_DEPOSIT_SUMMARY,
    ROUTE_GET_VAULT_ACCESS,
    ROUTE_UPDATE_VAULT_METADATA,
    USDC_GENESIS_TRUE_LP_PRICE,
    USDC_GENESIS_VAULT_ADDRESS
} from "../constants";

const LENDING_VAULT_POSITION_TAG = "LendingVault";
const LENDING_VAULT_METADATA_TAG = "LendingVaultMetadata";
const LENDING_VAULT_ACCESS_TAG = "LendingVaultAccess";

/** Response for `GET /lending_vaults/access` — whether the connected wallet may access the vault. */
export interface VaultAccessResponse {
    allowed: boolean;
}

const USDC_GENESIS_LP_DECIMALS = 1e6;

/**
 * Temporary incident patch: the server stamps USDC Genesis positions with the inflated share price, which
 * also swamps the response's filter-wide aggregate. Rebuilds both from the hardcoded price, valuing USDC at $1.
 * Corrects within one response because the price moves between requests. The aggregate is only corrected
 * when the Genesis row is on the returned page, so aggregate readers should sort by APY (it sorts first).
 */
export function correctGenesisVaultDeposits(response: VaultDepositResponse): VaultDepositResponse {
    const stamped = response.items.find((item) => item.vaultAddress === USDC_GENESIS_VAULT_ADDRESS);
    if (!stamped) return response;

    const sharesToUsd = (shares: number) => (shares / USDC_GENESIS_LP_DECIMALS) * (USDC_GENESIS_TRUE_LP_PRICE ?? 0);
    const positionValueUsd = sharesToUsd(stamped.totalLpShares);
    const corrected = {
        ...stamped,
        positionValueUsd,
        availableValueUsd: sharesToUsd(stamped.availableLpShares),
        collateralValueUsd: sharesToUsd(stamped.collateralLpShares),
        pnlUsd: stamped.pnlUsd - stamped.positionValueUsd + positionValueUsd,
        baseApy: 0,
        rewardApy: 0,
        collateralYieldApy: 0,
        totalApy: 0
    };

    // Genesis has no meaningful APY right now, so averages and daily interest only cover the other vaults
    const others = response.items.filter((item) => item !== stamped);
    const othersValueUsd = others.reduce((sum, item) => sum + item.positionValueUsd, 0);
    const weightedApy = (getApy: (item: (typeof others)[number]) => number) =>
        othersValueUsd > 0
            ? others.reduce((sum, item) => sum + item.positionValueUsd * getApy(item), 0) / othersValueUsd
            : 0;

    return {
        ...response,
        items: response.items.map((item) => (item === stamped ? corrected : item)),
        aggregate: {
            ...response.aggregate,
            totalValueUsd: response.aggregate.totalValueUsd - stamped.positionValueUsd + positionValueUsd,
            totalEarnedUsd: response.aggregate.totalEarnedUsd - stamped.pnlUsd + corrected.pnlUsd,
            expected24hInterestUsd: others.reduce(
                (sum, item) => sum + (item.positionValueUsd * item.totalApy) / 365,
                0
            ),
            wAvgBaseApy: weightedApy((item) => item.baseApy),
            wAvgRewardApy: weightedApy((item) => item.rewardApy),
            wAvgCollateralYieldApy: weightedApy((item) => item.collateralYieldApy),
            wAvgTotalApy: weightedApy((item) => item.totalApy)
        }
    };
}

export const lendingVaultApi = createApi({
    reducerPath: "abfLendingVaultApi",
    baseQuery: marketsBaseQuery,
    serializeQueryArgs: abfSerializeQueryArgs,

    tagTypes: [LENDING_VAULT_POSITION_TAG, LENDING_VAULT_METADATA_TAG, LENDING_VAULT_ACCESS_TAG],
    endpoints: (builder) => ({
        lendingVaultPositions: builder.query<VaultDepositResponse, UserLendingVaultFilter>({
            providesTags: [LENDING_VAULT_POSITION_TAG],
            query(params) {
                return {
                    url: ROUTE_GET_LENDING_USER_VAULT_POSITIONS,
                    method: "POST",
                    body: params
                };
            },
            transformResponse: (response: ApiVaultDepositResponse) =>
                correctGenesisVaultDeposits(parseVaultDepositResponseFromApi(response))
        }),
        userPositionDepositSummary: builder.query<UserPositionDepositSummaryResponse, UserPositionDepositSummaryParams>(
            {
                providesTags: [LENDING_VAULT_POSITION_TAG],
                query(params) {
                    return {
                        url: ROUTE_GET_USER_POSITION_DEPOSIT_SUMMARY,
                        method: "POST",
                        body: params
                    };
                }
            }
        ),
        userVaultExternalRewardsPnl: builder.query<
            UserVaultExternalRewardsPnlResponse | null | undefined,
            UserExternalRewardsFilter
        >({
            providesTags: [LENDING_VAULT_POSITION_TAG],
            query(params) {
                return {
                    url: ROUTE_GET_USER_EXTERNAL_REWARDS_PNL,
                    method: "POST",
                    body: params
                };
            },
            transformResponse: (
                response: UserVaultExternalRewardsPnlResponse | null | undefined
            ): UserVaultExternalRewardsPnlResponse | null | undefined => {
                if (response === null) return null;
                if (response === undefined) return undefined;
                if (Object.keys(response).length === 0) return null;
                return response;
            }
        }),
        vaultAccess: builder.query<VaultAccessResponse, { vaultAddress: string }>({
            providesTags: [LENDING_VAULT_ACCESS_TAG],
            query({ vaultAddress }) {
                return {
                    url: ROUTE_GET_VAULT_ACCESS,
                    method: "GET",
                    params: { vaultAddress }
                };
            }
        }),
        updateVaultMetadataWithSignature: builder.mutation<void, UpdateVaultMetadataParams>({
            invalidatesTags: [LENDING_VAULT_METADATA_TAG],
            query(params) {
                return {
                    url: ROUTE_UPDATE_VAULT_METADATA,
                    method: "POST",
                    body: params
                };
            }
        })
    })
});

export const {
    useLendingVaultPositionsQuery,
    useUserPositionDepositSummaryQuery,
    useUserVaultExternalRewardsPnlQuery,
    useVaultAccessQuery,
    useUpdateVaultMetadataWithSignatureMutation
} = lendingVaultApi;

export function useLendingVaultApi() {
    const dispatch = useDispatch();

    return {
        resetLendingVaultPositionsApi: () =>
            dispatch(lendingVaultApi.util.invalidateTags([LENDING_VAULT_POSITION_TAG])),
        resetLendingVaultMetadataApi: () => dispatch(lendingVaultApi.util.invalidateTags([LENDING_VAULT_METADATA_TAG])),
        resetLendingVaultAccessApi: () => dispatch(lendingVaultApi.util.invalidateTags([LENDING_VAULT_ACCESS_TAG]))
    };
}
